import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Map as CustomMap, Marker, Popup, FeatureGroup, Polygon, LayersControl,
} from 'react-leaflet';
import L from 'leaflet';
import { useNavigate } from 'react-router-dom';
import { GoogleLayer } from 'react-leaflet-google-v2';
import polylabel from 'polylabel';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import './GeoMapSummary.scss';
import mapcheckcustom from './mapcheckcustom.svg';
import { brazilLocation } from '../../../../utils/LocationUtils';

import {
  get, isSuperAgentError, ResponseWrapper, RequestWrapper,
} from '../../../../dataSource/requests';
import { MAP_API_KEY, MAP_CHANNEL_VERSION } from '../../../../utils/constants';

const iconeSelecionado = new L.Icon({
  iconUrl: mapcheckcustom,
  iconAnchor: [13, 26],
  popupAnchor: [0, 0],
  iconSize: [26, 52],
});

const GeoMapSummary = (props): JSX.Element => {
  const { params } = props;
  const mapReference = useRef(null);
  const navigate = useNavigate();
  const [propriedades] = useState(params.selectedProperty.id);
  const [talhoes, setTalhoes] = useState([]);
  const [markersTalhoesSelecionados, setMarkersTalhoesSelecionados] = useState([]);
  const [positionLocal, setPositionLocal] = useState<Array<number>>(brazilLocation);

  const handleSelectedLocation = (lat: number, lng: number): void => {
    setPositionLocal([lat, lng]);
  };

  const selecionarTalhoes = (): void => {
    const newMarkersTalhoesSelecionados = [];

    const talhoesValues = Object.values(params.talhoesSelecionados);

    talhoesValues.forEach((talhao) => {
      if (talhao.landId === propriedades) {
        const coordinatesTalhao = [talhao.polygon.map((ponto) => [ponto.lon, ponto.lat])];

        const centroVerdadeiro = polylabel(coordinatesTalhao, 1.0);

        newMarkersTalhoesSelecionados.push({
          culture: talhao.culture,
          name: talhao.name,
          coordinates: [centroVerdadeiro[1], centroVerdadeiro[0]],
        });
      }
    });

    setMarkersTalhoesSelecionados(newMarkersTalhoesSelecionados);
  };

  useEffect(() => {
    const getInitialLocation = (): void => {
      if (params.talhoesSelecionados[0].polygon[0] == null) {
        window.navigator.geolocation.getCurrentPosition(
          (newPosition) => {
            handleSelectedLocation(newPosition.coords.latitude, newPosition.coords.longitude);
          },
        );
      }
    };

    const buscarTalhoes = async (): Promise<void> => {
      const land = params.selectedProperty;

      let response: ResponseWrapper | undefined;
      let responseError: ResponseWrapper | undefined;
      let genericError: Error | undefined;

      const requestReference: RequestWrapper = get({
        url: `/land/retrieveLandFieldsByLandAndCulture?landId=${land.id}`,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        response = await requestReference;
      } catch (error) {
        console.log(error);

        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      switch (true) {
        case (response?.body?.code === 6001):
          // "Land fields retrieved by land successfully", 6001
          if (response?.body.landFields.length > 0) {
            const landFields = [];

            response?.body.landFields.forEach((landField) => {
              const pontos = [];

              landField.polygon.forEach((landFieldEach) => {
                pontos.push([landFieldEach.lat, landFieldEach.lon]);
              });

              pontos.pop();
              landFields.push({ key: landField.id, pontos });
            });

            setTalhoes(landFields);
          }
          break;

        case (responseError?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          break;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        default:
          break;
      }
    };

    getInitialLocation();
    buscarTalhoes();
    selecionarTalhoes();

    return () => {
      mapReference.current = null;
    };
  }, []);

  useEffect(() => {
    mapReference.current?.leafletElement?.invalidateSize();

    return () => {
      mapReference.current = mapReference.current;
    };
  });

  const desenhoTalhoes = (): JSX.Element[] => {
    return talhoes.map((value) => {
      if (params.talhoesSelecionados.find((v) => value.key === v.id)) {
        return (
          <Polygon
            key={`talhao-${value.key}`}
            _leaflet_id={value.key}
            positions={value.pontos}
            color="#fabe46"
            opacity="1.0"
            fillColor="rgba(36, 101, 74, 0.6)"
            fillOpacity="1.0"
          />
        );
      }
      return (
        <Polygon
          key={`talhao-${value.key}`}
          _leaflet_id={value.key}
          positions={value.pontos}
          color="#fabe46"
          opacity="1.0"
          fillColor="rgba(216, 216, 216, 0.6)"
          fillOpacity="1.0"
        />
      );
    });
  };

  const position = [
    Number.parseFloat(params.selectedProperty.location.lat
      || params.talhoesSelecionados[0].polygon[0].lat
      || positionLocal[0]
      || -10.000000,
    ),
    Number.parseFloat(params.selectedProperty.location.lon
      || params.talhoesSelecionados[0].polygon[0].lon
      || positionLocal[1]
      || -55.000000,
    ),
  ];

  return (
    <div id="mapa-add-talhao" className="GeoMapSummary">
      <CustomMap
        id="map-google"
        ref={mapReference}
        center={position}
        zoom={13}
      >
        <LayersControl>
          <LayersControl.BaseLayer
            checked
            name="Google Maps Satellite"
          >
            <GoogleLayer
              googlekey={MAP_API_KEY}
              maptype="HYBRID"
              version={MAP_CHANNEL_VERSION}
            />
          </LayersControl.BaseLayer>
        </LayersControl>
        <FeatureGroup>
          {desenhoTalhoes()}
        </FeatureGroup>
        {
          markersTalhoesSelecionados.map(
            (v, index) => (
              <Marker
                key={`selecionado-${index}`}
                position={v.coordinates}
                icon={iconeSelecionado}
              >
                <Popup>{`${v.name} - ${v.culture.description}`}</Popup>
              </Marker>
            ),
          )
        }
      </CustomMap>
    </div>
  );
};

GeoMapSummary.propTypes = {
  params: PropTypes.shape({
    selectedProperty: PropTypes.objectOf(PropTypes.any),
    talhoesSelecionados: PropTypes.any,

    selectedService: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),

    selectedType: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  }),
};

GeoMapSummary.defaultProps = {
  params: {},
};

export default GeoMapSummary;
